import { REPAIR_ITEM_OPTIONS, normalizeRepairItems } from '../constants/repair-items';
import {
  DEFAULT_AWAITING_RX_FOLLOW_UP_HOURS,
  DEFAULT_INQUIRY_FOLLOW_UP_HOURS,
  IN_HOUSE_REPAIR_LOCATION,
  addHours,
  isOutsideRepairLocation,
} from '../constants/task-lists';
import { FIRST_INSPECTION_NEEDED, ORDER_LENSES_ACTION, isOpenTaskStatus as isLegacyOpenTask } from './job-actions';

export type TaskKind = 'open' | 'awaiting' | 'completed';

export interface TaskImage {
  name?: string;
  dataUrl?: string;
  kind?: 'condition' | 'invoice';
}

export interface WorkflowEntry {
  id?: string;
  kind?: TaskKind;
  date?: string | Date;
  status?: string;
  branch?: string;
  notes?: string;
  repairItems?: string[];
  waitingOn?: string;
  vendor?: string;
  followUpDate?: string | Date;
  expectedDate?: string | Date;
  followUpNumber?: number;
  activityChoice?: string;
  reopenedFrom?: string;
  sourceId?: string;
  description?: string;
  referenceNumber?: string;
  carrier?: string;
  trackingNumber?: string;
  labVendor?: string;
  repairLocation?: string;
  estimate?: string;
  estimateApprovalRequired?: boolean;
  approvalStatus?: string;
  otherText?: string;
  whoToCall?: string;
  images?: TaskImage[];
  eta?: string | Date;
  completed?: boolean;
  completedBy?: string;
  completedAt?: string | Date;
}

export interface WorkflowContext {
  jobActions?: string[];
  status?: string;
  physicalStatus?: string;
  lensStatus?: string;
  nosepadInstall?: boolean;
  partOther?: boolean;
  partOtherDescription?: string;
  repairItems?: string[];
  repairLocation?: string;
  enclosed?: boolean;
  frameOnly?: boolean;
  followUpDate?: string | Date;
  expectedDate?: string | Date;
  eta?: string | Date;
  referenceNumber?: string;
  labVendor?: string;
  inquiryFollowUpHours?: number;
  awaitingRxFollowUpHours?: number;
  whoToCall?: string;
  now?: Date;
}

export interface CompleteTaskInput {
  id?: string;
  index?: number;
  initials: string;
  notes?: string;
  repairItems?: string[];
  eta?: string | Date;
  referenceNumber?: string;
  followUpDate?: string | Date;
  expectedDate?: string | Date;
  waitingOn?: string;
  vendor?: string;
  labVendor?: string;
  repairLocation?: string;
  estimate?: string;
  estimateApprovalRequired?: boolean;
  otherText?: string;
  whoToCall?: string;
  images?: TaskImage[];
  carrier?: string;
  trackingNumber?: string;
  activityChoice?: string;
  description?: string;
  now?: Date;
}

export interface StackPatch {
  destination: string;
  status?: string;
  todo?: string;
  rxStatus?: string;
  lensStatus?: string;
  physicalStatus?: string;
  eta?: string | Date;
  referenceNumber?: string;
  followUpDate?: string | Date;
  expectedDate?: string | Date;
  labVendor?: string;
  repairLocation?: string;
  jobActionsRemove?: string[];
}

export interface WorkflowResult {
  entries: WorkflowEntry[];
  destination: string;
  logItem: string;
  logNotes: string;
  patch: StackPatch;
}

export const REPAIR_ACTIONS = ['REPAIRED', 'ORDER PART', 'SENT TO REPAIR'];

export const APPROVAL_ACTIONS = ['APPROVED', 'DECLINED', 'LEFT MESSAGE'];

export const AWAITING_ACTIVITY_OPTIONS = ['RECEIVED', 'RESPONSE RECEIVED', 'RX RECEIVED'];

const REPAIR_ABBREV: Record<string, string> = {
  'Left Temple': 'LT. TEMPLE',
  'Right Temple': 'RT. TEMPLE',
  'Left Hinge': 'LT. HINGE',
  'Right Hinge': 'RT. HINGE',
  Bridge: 'BRIDGE',
  'Frame Front': 'FRAME FRONT',
  'Nosepad Replacement': 'NOSEPAD REPLACEMENT',
  'Nosepad Addition': 'NOSEPAD INSTALL',
  'Nosepad Install / Addition': 'NOSEPAD INSTALL',
  Other: 'OTHER',
};

export function newActivityId(): string {
  return (
    'ta_' +
    Date.now().toString(36) +
    '_' +
    Math.random().toString(36).slice(2, 8)
  );
}

export function parseActivityDate(value: any): Date | null {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  if (isNaN(date.getTime())) {
    return null;
  }
  return date;
}

export function startOfDay(value?: Date): Date {
  const date = value ? new Date(value.getTime()) : new Date();
  date.setHours(0, 0, 0, 0);
  return date;
}

export function isValidInitials(value?: string): boolean {
  return /^[A-Za-z]{1,4}$/.test(String(value || '').trim());
}

export function normalizeInitials(value?: string): string {
  return String(value || '').trim().toUpperCase();
}

export function isAwaitingStatus(status?: string): boolean {
  return /^AWAITING /i.test(String(status || '').trim());
}

export function isFollowUpTask(status?: string): boolean {
  return /^FOLLOW UP \//i.test(String(status || '').trim());
}

export function isFollowedUpStatus(status?: string): boolean {
  return /^FOLLOWED UP \//i.test(String(status || '').trim());
}

export function canonicalOpenStatus(status?: string): string {
  const value = String(status || '').trim();
  if (!value) {
    return '';
  }
  if (value === 'CALL FOR RX' || value === 'CALL / RX') {
    return 'CALL / RX';
  }
  if (value === 'TO ORDER' || value === 'ORDER FRAME') {
    return 'ORDER FRAME';
  }
  if (value === FIRST_INSPECTION_NEEDED || value === 'FIRST INSPECTION') {
    return 'FIRST INSPECTION';
  }
  if (value === 'CHECK FRAME ETA') {
    return 'FOLLOW UP / FRAME';
  }
  return value;
}

export function classifyKind(entry: WorkflowEntry): TaskKind {
  if (entry?.kind === 'open' || entry?.kind === 'awaiting' || entry?.kind === 'completed') {
    if (entry.kind === 'open' && entry.completed) {
      return 'completed';
    }
    if (entry.kind === 'awaiting' && entry.completed) {
      return 'completed';
    }
    return entry.kind;
  }
  const status = String(entry?.status || '').trim();
  if (isAwaitingStatus(status) && !entry?.completed) {
    return 'awaiting';
  }
  if (isWorkflowOpenStatus(status) && !entry?.completed) {
    return 'open';
  }
  return 'completed';
}

export function isWorkflowOpenStatus(status?: string): boolean {
  const value = String(status || '').trim();
  if (!value) {
    return false;
  }
  if (isFollowUpTask(value)) {
    return true;
  }
  if (isLegacyOpenTask(value)) {
    return true;
  }
  return [
    'ORDER FRAME',
    'TO ORDER',
    'ORDER PART',
    'OTHER / PART',
    'FIRST INSPECTION',
    'CUT & EDGE',
    'FINAL INSPECTION',
    'NOTIFY PATIENT',
    'SHIP',
    'FOLLOW UP / RX',
    'FOLLOW UP / FRAME',
    'FOLLOW UP / PART',
    'FOLLOW UP / REPAIR',
    'FOLLOW UP / INQUIRY',
    'FOLLOW UP / APPROVAL',
    'HOLD / RX',
    'HOLD',
    'INQUIRY',
    'CUSTOM LENSES',
  ].includes(canonicalOpenStatus(value));
}

export function branchForStatus(status?: string): string {
  const value = canonicalOpenStatus(status) || String(status || '').trim().toUpperCase();
  if (/APPROVAL/.test(value)) {
    return 'APPROVAL';
  }
  if (/INQUIRY/.test(value) || value === 'AWAITING RESPONSE') {
    return 'INQUIRY';
  }
  if (/RX/.test(value)) {
    return 'RX';
  }
  if (/PART/.test(value) && !/OTHER/.test(value)) {
    return 'PART';
  }
  if (/REPAIR/.test(value)) {
    return 'REPAIR';
  }
  if (/LENS/.test(value)) {
    return 'LENSES';
  }
  if (/FRAME/.test(value) || value === 'ORDERED' || value === 'TO ORDER') {
    return 'FRAME';
  }
  if (/NOSEPAD/.test(value)) {
    return 'REPAIR';
  }
  if (/OTHER/.test(value)) {
    return 'REPAIR';
  }
  if (value === 'HOLD') {
    return 'HOLD';
  }
  return 'GENERAL';
}

export function followUpTaskName(branch: string, n: number): string {
  const key = String(branch || '').toUpperCase();
  return n <= 1 ? `FOLLOW UP / ${key}` : `FOLLOW UP / ${key}${n}`;
}

export function followedUpLabel(branch: string, n: number): string {
  const key = String(branch || '').toUpperCase();
  return n <= 1 ? `FOLLOWED UP / ${key}` : `FOLLOWED UP / ${key}${n}`;
}

export function followUpNumberFromStatus(status?: string): number {
  const value = String(status || '').trim();
  const match = value.match(/\/\s*(RX|FRAME|PART|REPAIR|INQUIRY|APPROVAL)(\d+)?$/i);
  if (!match) {
    return 0;
  }
  return match[2] ? parseInt(match[2], 10) : 1;
}

export function nextFollowUpNumber(entries: WorkflowEntry[], branch: string): number {
  let max = 0;
  (entries || []).forEach((entry) => {
    const status = String(entry?.status || '');
    if (!new RegExp(`${branch}\\d*$`, 'i').test(status)) {
      return;
    }
    if (isFollowUpTask(status) || isFollowedUpStatus(status)) {
      max = Math.max(max, followUpNumberFromStatus(status) || 1);
    }
  });
  return max + 1;
}

export function activityEntryDefaults(
  entry: Partial<WorkflowEntry> = {}
): WorkflowEntry {
  const status = String(entry.status || '');
  const merged: WorkflowEntry = {
    id: entry.id || newActivityId(),
    date: entry.date || new Date(),
    status,
    branch: entry.branch || branchForStatus(status),
    notes: entry.notes || '',
    repairItems: normalizeRepairItems(
      Array.isArray(entry.repairItems) ? entry.repairItems.slice() : []
    ),
    waitingOn: entry.waitingOn || '',
    vendor: entry.vendor || '',
    followUpDate: entry.followUpDate || '',
    expectedDate: entry.expectedDate || '',
    followUpNumber: entry.followUpNumber || followUpNumberFromStatus(status) || 0,
    activityChoice: entry.activityChoice || '',
    reopenedFrom: entry.reopenedFrom || '',
    sourceId: entry.sourceId || '',
    description: entry.description || '',
    referenceNumber: entry.referenceNumber || '',
    carrier: entry.carrier || '',
    trackingNumber: entry.trackingNumber || '',
    labVendor: entry.labVendor || '',
    repairLocation: entry.repairLocation || IN_HOUSE_REPAIR_LOCATION,
    estimate: entry.estimate || '',
    estimateApprovalRequired: !!entry.estimateApprovalRequired,
    approvalStatus: entry.approvalStatus || '',
    otherText: entry.otherText || '',
    whoToCall: entry.whoToCall || '',
    images: Array.isArray(entry.images) ? entry.images.slice() : [],
    eta: entry.eta || '',
    completed: !!entry.completed,
    completedBy: entry.completedBy || '',
    completedAt: entry.completedAt || '',
    kind: entry.kind,
  };
  merged.kind = classifyKind(merged);
  return merged;
}

export function findOpenIndex(
  entries: WorkflowEntry[],
  status: string
): number {
  const wanted = canonicalOpenStatus(status);
  return (entries || []).findIndex(
    (entry) =>
      classifyKind(entry) === 'open' &&
      canonicalOpenStatus(entry.status) === wanted
  );
}

export function findAwaitingIndex(
  entries: WorkflowEntry[],
  status: string
): number {
  const wanted = String(status || '').trim().toUpperCase();
  return (entries || []).findIndex(
    (entry) =>
      classifyKind(entry) === 'awaiting' &&
      String(entry.status || '').trim().toUpperCase() === wanted
  );
}

export function hasOpenTask(entries: WorkflowEntry[], status: string): boolean {
  return findOpenIndex(entries, status) >= 0;
}

export function hasAwaiting(entries: WorkflowEntry[], status: string): boolean {
  return findAwaitingIndex(entries, status) >= 0;
}

export function ensureOpenTask(
  entries: WorkflowEntry[],
  status: string,
  extra: Partial<WorkflowEntry> = {}
): WorkflowEntry[] {
  const next = (entries || []).map((entry) => activityEntryDefaults(entry));
  const canonical = canonicalOpenStatus(status);
  if (!canonical) {
    return next;
  }
  if (hasOpenTask(next, canonical)) {
    const index = findOpenIndex(next, canonical);
    if (index >= 0 && extra && Object.keys(extra).length) {
      next[index] = activityEntryDefaults({
        ...next[index],
        ...extra,
        status: canonical,
        kind: 'open',
        completed: false,
      });
    }
    return next;
  }
  next.push(
    activityEntryDefaults({
      ...extra,
      status: canonical,
      kind: 'open',
      completed: false,
      date: extra.date || new Date(),
    })
  );
  return next;
}

export function ensureAwaiting(
  entries: WorkflowEntry[],
  status: string,
  extra: Partial<WorkflowEntry> = {}
): WorkflowEntry[] {
  const next = (entries || []).map((entry) => activityEntryDefaults(entry));
  const label = String(status || '').trim();
  if (!label) {
    return next;
  }
  if (hasAwaiting(next, label)) {
    const index = findAwaitingIndex(next, label);
    if (index >= 0 && extra && Object.keys(extra).length) {
      next[index] = activityEntryDefaults({
        ...next[index],
        ...extra,
        status: label,
        kind: 'awaiting',
        completed: false,
      });
    }
    return next;
  }
  next.push(
    activityEntryDefaults({
      ...extra,
      status: label,
      kind: 'awaiting',
      completed: false,
      date: extra.date || new Date(),
    })
  );
  return next;
}

function pushCompleted(
  entries: WorkflowEntry[],
  status: string,
  extra: Partial<WorkflowEntry>,
  initials: string,
  now: Date
): WorkflowEntry[] {
  const next = entries.slice();
  next.push(
    activityEntryDefaults({
      ...extra,
      status,
      kind: 'completed',
      completed: true,
      completedBy: initials,
      completedAt: now,
      date: now,
    })
  );
  return next;
}

function closeOpenAt(
  entries: WorkflowEntry[],
  index: number
): WorkflowEntry[] {
  const next = entries.slice();
  if (index < 0 || index >= next.length) {
    return next;
  }
  next.splice(index, 1);
  return next;
}

function closeAwaiting(
  entries: WorkflowEntry[],
  status: string
): WorkflowEntry[] {
  return entries.filter(
    (entry) =>
      !(
        classifyKind(entry) === 'awaiting' &&
        String(entry.status || '').trim().toUpperCase() ===
          String(status || '').trim().toUpperCase()
      )
  );
}

function cancelFollowUpTasks(
  entries: WorkflowEntry[],
  branch: string
): WorkflowEntry[] {
  const pattern = new RegExp(`^FOLLOW UP \\/ ${branch}\\d*$`, 'i');
  return entries.filter(
    (entry) =>
      !(classifyKind(entry) === 'open' && pattern.test(String(entry.status || '')))
  );
}

export function abbreviateRepairItem(item: string): string {
  return REPAIR_ABBREV[item] || String(item || '').toUpperCase();
}

export function completedDisplayLabel(entry: WorkflowEntry): string {
  const status = String(entry?.status || '').trim();
  const items = Array.isArray(entry?.repairItems) ? entry.repairItems.filter(Boolean) : [];
  if ((status === 'REPAIRED' || status === 'REPAIR') && items.length) {
    return `REPAIRED (${items.map(abbreviateRepairItem).join(', ')})`;
  }
  if (status === 'OTHER / PART' && entry?.description) {
    return `OTHER / PART (${entry.description})`;
  }
  if (status === 'ORDERED' && entry?.referenceNumber) {
    return `ORDERED`;
  }
  return status;
}

export function formatCompletedDate(value: any, locale = 'en-US'): string {
  const date = parseActivityDate(value);
  if (!date) {
    return '';
  }
  return date.toLocaleDateString(locale, {
    month: 'numeric',
    day: 'numeric',
    year: '2-digit',
  });
}

export function completedHistoryLine(entry: WorkflowEntry, locale = 'en-US'): string {
  const date = formatCompletedDate(entry?.completedAt || entry?.date, locale);
  const label = completedDisplayLabel(entry);
  const initials = String(entry?.completedBy || '').trim().toUpperCase();
  return [date, label, initials].filter(Boolean).join(' — ');
}

export function openTaskRows(entries: WorkflowEntry[]): Array<{
  index: number;
  entry: WorkflowEntry;
}> {
  return (entries || [])
    .map((entry, index) => ({ index, entry: activityEntryDefaults(entry) }))
    .filter((row) => classifyKind(row.entry) === 'open');
}

export function awaitingRows(entries: WorkflowEntry[]): Array<{
  index: number;
  entry: WorkflowEntry;
}> {
  return (entries || [])
    .map((entry, index) => ({ index, entry: activityEntryDefaults(entry) }))
    .filter((row) => classifyKind(row.entry) === 'awaiting');
}

export function completedRows(entries: WorkflowEntry[]): Array<{
  index: number;
  entry: WorkflowEntry;
}> {
  return (entries || [])
    .map((entry, index) => ({ index, entry: activityEntryDefaults(entry) }))
    .filter((row) => classifyKind(row.entry) === 'completed')
    .reverse();
}

export function openTaskFields(status?: string): string[] {
  const value = canonicalOpenStatus(status);
  if (value === 'ORDER FRAME' || value === 'ORDER PART') {
    return ['eta', 'referenceNumber', 'notes'];
  }
  if (value === 'CALL / RX' || value === 'HOLD / RX' || isFollowUpTask(value)) {
    return ['followUpDate', 'expectedDate', 'waitingOn', 'whoToCall', 'contact', 'notes'];
  }
  if (value === 'REPAIR' || value === 'CUSTOM LENSES') {
    return [
      'repairLocation',
      'repairItems',
      'followUpDate',
      'estimate',
      'estimateApprovalRequired',
      'referenceNumber',
      'notes',
      'images',
      'activityChoice',
    ];
  }
  if (value === 'OTHER / PART') {
    return ['description', 'notes'];
  }
  if (value === 'INQUIRY' || value === 'HOLD') {
    return ['notes', 'activityChoice'];
  }
  if (value === 'SHIP') {
    return ['carrier', 'trackingNumber', 'notes'];
  }
  if (/FOLLOW UP \/ REPAIR/.test(value) || value === 'FOLLOW UP / REPAIR') {
    return ['followUpDate', 'labVendor', 'referenceNumber', 'notes'];
  }
  if (/FOLLOW UP \/ FRAME/.test(value) || value === 'FOLLOW UP / FRAME') {
    return ['followUpDate', 'eta', 'referenceNumber', 'notes'];
  }
  if (/FOLLOW UP \/ PART/.test(value) || value === 'FOLLOW UP / PART') {
    return ['followUpDate', 'referenceNumber', 'notes'];
  }
  return ['notes'];
}

export function awaitingFields(status?: string): string[] {
  const value = String(status || '').trim().toUpperCase();
  if (value === 'AWAITING FRAME') {
    return ['vendor', 'eta', 'referenceNumber', 'followUpDate', 'notes'];
  }
  if (value === 'AWAITING RX') {
    return ['waitingOn', 'followUpDate', 'expectedDate', 'notes'];
  }
  if (value === 'AWAITING REPAIR') {
    return ['repairLocation', 'labVendor', 'referenceNumber', 'eta', 'followUpDate', 'notes'];
  }
  if (value === 'AWAITING PART') {
    return ['referenceNumber', 'eta', 'followUpDate', 'notes'];
  }
  if (value === 'AWAITING LENSES') {
    return ['followUpDate', 'notes'];
  }
  if (value === 'AWAITING RESPONSE') {
    return ['followUpDate', 'notes'];
  }
  return ['followUpDate', 'notes'];
}

export function openTaskActions(status?: string, entry?: WorkflowEntry): string[] {
  const value = canonicalOpenStatus(status);
  if (value === 'REPAIR' || value === 'CUSTOM LENSES') {
    if (needsApproval(entry)) {
      return APPROVAL_ACTIONS.slice();
    }
    if (value === 'CUSTOM LENSES') {
      return ['ORDERED', 'REPAIRED'];
    }
    if (isOutsideRepairLocation(entry?.repairLocation || entry?.labVendor)) {
      return ['SENT TO REPAIR'];
    }
    return ['REPAIRED', 'ORDER PART'];
  }
  if (value === 'INQUIRY') {
    return ['INQUIRED'];
  }
  if (value === 'CALL / RX') {
    return ['CALL NEEDED', 'CALLED FOR RX', 'RX RECEIVED'];
  }
  if (value === 'HOLD / RX') {
    return ['CALLED FOR RX', 'RX RECEIVED'];
  }
  if (isFollowUpTask(value) && branchForStatus(value) === 'APPROVAL') {
    return APPROVAL_ACTIONS.slice();
  }
  return [];
}

export function awaitingActions(status?: string): string[] {
  const value = String(status || '').trim().toUpperCase();
  if (value === 'AWAITING RESPONSE') {
    return ['RESPONSE RECEIVED'];
  }
  if (value === 'AWAITING RX') {
    return ['RX RECEIVED'];
  }
  return ['RECEIVED'];
}

export function needsApproval(entry?: WorkflowEntry): boolean {
  return (
    !!entry?.estimateApprovalRequired &&
    String(entry?.approvalStatus || '').trim().toUpperCase() !== 'APPROVED'
  );
}

function defaultOutcome(status: string, choice?: string): string {
  const value = canonicalOpenStatus(status);
  const action = String(choice || '').trim().toUpperCase();
  if (value === 'REPAIR' || value === 'CUSTOM LENSES') {
    if (action === 'ORDER PART') {
      return 'ORDER PART';
    }
    if (action === 'SEND FOR REPAIR' || action === 'SENT TO REPAIR' || action === 'SENT FOR REPAIR') {
      return 'SENT TO REPAIR';
    }
    if (action === 'APPROVED') {
      return 'CUSTOMER APPROVED';
    }
    if (action === 'DECLINED') {
      return 'CUSTOMER DECLINED';
    }
    if (action === 'LEFT MESSAGE') {
      return 'LEFT MESSAGE';
    }
    if (action === 'ORDERED') {
      return 'ORDERED';
    }
    return 'REPAIRED';
  }
  if (value === 'INQUIRY') {
    return action === 'INQUIRED' ? 'INQUIRED' : 'INQUIRED';
  }
  if (value === 'CALL / RX' || value === 'HOLD / RX') {
    if (action === 'RX RECEIVED') {
      return 'RX RECEIVED';
    }
    return 'CALLED FOR RX';
  }
  if (isFollowUpTask(value)) {
    const n = followUpNumberFromStatus(value) || 1;
    const branch = branchForStatus(value);
    return followedUpLabel(branch, n);
  }
  if (value === 'ORDER FRAME' || value === 'ORDER PART') {
    return 'ORDERED';
  }
  if (value === 'ORDER LENSES') {
    return 'ORDERED LENSES';
  }
  if (value === 'FIRST INSPECTION') {
    return 'FIRST INSPECTION';
  }
  if (value === 'INSTALL NOSEPADS') {
    return 'INSTALLED NOSEPADS';
  }
  if (value === 'OTHER / PART') {
    return 'OTHER / PART';
  }
  if (value === 'SHIP') {
    return 'SHIPPED';
  }
  return value;
}

function locateEntry(
  entries: WorkflowEntry[],
  input: { id?: string; index?: number }
): number {
  if (input.id) {
    const byId = entries.findIndex((entry) => entry.id === input.id);
    if (byId >= 0) {
      return byId;
    }
  }
  if (typeof input.index === 'number') {
    return input.index;
  }
  return -1;
}

export function completeOpenTask(
  entries: WorkflowEntry[],
  input: CompleteTaskInput,
  context: WorkflowContext = {}
): WorkflowResult {
  const initials = normalizeInitials(input.initials);
  const now = input.now || new Date();
  let next = (entries || []).map((entry) => activityEntryDefaults(entry));
  const index = locateEntry(next, input);
  const empty: WorkflowResult = {
    entries: next,
    destination: 'SAVE',
    logItem: '',
    logNotes: '',
    patch: { destination: 'SAVE' },
  };
  if (index < 0 || classifyKind(next[index]) !== 'open') {
    return empty;
  }
  const open = next[index];
  const taskStatus = canonicalOpenStatus(open.status);
  const choice = String(input.activityChoice || '').trim();
  const details: Partial<WorkflowEntry> = {
    notes: input.notes !== undefined ? input.notes : open.notes,
    repairItems: normalizeRepairItems(
      input.repairItems !== undefined ? input.repairItems : open.repairItems
    ),
    eta: input.eta !== undefined ? input.eta : open.eta || context.eta,
    referenceNumber:
      input.referenceNumber !== undefined
        ? input.referenceNumber
        : open.referenceNumber,
    followUpDate:
      input.followUpDate !== undefined
        ? input.followUpDate
        : open.followUpDate || context.followUpDate,
    expectedDate:
      input.expectedDate !== undefined ? input.expectedDate : open.expectedDate,
    waitingOn: input.waitingOn !== undefined ? input.waitingOn : open.waitingOn,
    vendor: input.vendor !== undefined ? input.vendor : open.vendor,
    labVendor: input.labVendor !== undefined ? input.labVendor : open.labVendor,
    repairLocation:
      input.repairLocation !== undefined
        ? input.repairLocation
        : open.repairLocation,
    estimate: input.estimate !== undefined ? input.estimate : open.estimate,
    estimateApprovalRequired:
      input.estimateApprovalRequired !== undefined
        ? input.estimateApprovalRequired
        : open.estimateApprovalRequired,
    otherText: input.otherText !== undefined ? input.otherText : open.otherText,
    whoToCall: input.whoToCall !== undefined ? input.whoToCall : open.whoToCall,
    images: input.images !== undefined ? input.images : open.images,
    carrier: input.carrier !== undefined ? input.carrier : open.carrier,
    trackingNumber:
      input.trackingNumber !== undefined
        ? input.trackingNumber
        : open.trackingNumber,
    description:
      input.description !== undefined ? input.description : open.description,
    approvalStatus: open.approvalStatus,
    branch: open.branch || branchForStatus(taskStatus),
    sourceId: open.id,
  };

  if (taskStatus === 'REPAIR' && choice === 'ORDER PART') {
    next[index] = activityEntryDefaults({
      ...open,
      ...details,
      status: 'REPAIR',
      kind: 'open',
      completed: false,
    });
    next = ensureOpenTask(next, 'ORDER PART', {
      notes: details.notes,
      description: details.description,
    });
    return {
      entries: next,
      destination: 'SAVE',
      logItem: 'ORDER PART TASK CREATED',
      logNotes: String(details.notes || ''),
      patch: { destination: 'SAVE' },
    };
  }

  const approvalChoice = String(choice || '').trim().toUpperCase();
  if (
    (taskStatus === 'REPAIR' || taskStatus === 'CUSTOM LENSES') &&
    (approvalChoice === 'APPROVED' ||
      approvalChoice === 'DECLINED' ||
      approvalChoice === 'LEFT MESSAGE')
  ) {
    const completedLabel =
      approvalChoice === 'APPROVED'
        ? 'CUSTOMER APPROVED'
        : approvalChoice === 'DECLINED'
        ? 'CUSTOMER DECLINED'
        : 'LEFT MESSAGE';
    next = pushCompleted(next, completedLabel, details, initials, now);
    if (approvalChoice === 'DECLINED') {
      next = closeOpenAt(next, index);
      return {
        entries: next,
        destination: 'SAVE',
        logItem: completedLabel,
        logNotes: String(details.notes || ''),
        patch: { destination: 'SAVE' },
      };
    }
    next[index] = activityEntryDefaults({
      ...open,
      ...details,
      approvalStatus: approvalChoice === 'APPROVED' ? 'APPROVED' : open.approvalStatus,
      followUpDate:
        approvalChoice === 'LEFT MESSAGE'
          ? addHours(now, context.inquiryFollowUpHours || DEFAULT_INQUIRY_FOLLOW_UP_HOURS)
          : open.followUpDate,
      status: taskStatus,
      kind: 'open',
      completed: false,
    });
    return {
      entries: next,
      destination: 'SAVE',
      logItem: completedLabel,
      logNotes: String(details.notes || ''),
      patch: {
        destination: 'SAVE',
        followUpDate: next[index].followUpDate,
      },
    };
  }

  if (
    (taskStatus === 'HOLD / RX' || taskStatus === 'CALL / RX') &&
    approvalChoice === 'CALL NEEDED'
  ) {
    next[index] = activityEntryDefaults({
      ...open,
      ...details,
      activityChoice: 'CALL NEEDED',
      status: taskStatus,
      kind: 'open',
      completed: false,
    });
    return {
      entries: next,
      destination: 'SAVE',
      logItem: 'CALL NEEDED',
      logNotes: String(details.notes || ''),
      patch: {
        destination: 'SAVE',
        rxStatus: taskStatus === 'CALL / RX' ? 'CALL FOR RX' : open.status,
      },
    };
  }

  if (
    (taskStatus === 'HOLD / RX' || taskStatus === 'CALL / RX') &&
    approvalChoice === 'RX RECEIVED'
  ) {
    next = closeOpenAt(next, index);
    next = cancelFollowUpTasks(next, 'RX');
    next = pushCompleted(next, 'RX RECEIVED', details, initials, now);
    return {
      entries: next,
      destination: 'RX RECEIVED',
      logItem: 'RX RECEIVED',
      logNotes: String(details.notes || ''),
      patch: {
        destination: 'RX RECEIVED',
        rxStatus: '',
        followUpDate: '',
        jobActionsRemove: ['HOLD / RX', 'CALL FOR RX'],
      },
    };
  }

  const outcome = defaultOutcome(taskStatus, choice);
  next = closeOpenAt(next, index);
  next = pushCompleted(next, outcome, details, initials, now);

  let destination = 'SAVE';
  let awaiting = '';
  const patch: StackPatch = { destination: 'SAVE' };
  const rxFollowHours =
    context.awaitingRxFollowUpHours || DEFAULT_AWAITING_RX_FOLLOW_UP_HOURS;
  const inquiryHours =
    context.inquiryFollowUpHours || DEFAULT_INQUIRY_FOLLOW_UP_HOURS;

  if (
    taskStatus === 'CALL / RX' ||
    taskStatus === 'HOLD / RX' ||
    (isFollowUpTask(taskStatus) && details.branch === 'RX')
  ) {
    const followUp = addHours(now, rxFollowHours);
    next = ensureAwaiting(next, 'AWAITING RX', {
      waitingOn: details.waitingOn,
      whoToCall: details.whoToCall,
      followUpDate: followUp,
      expectedDate: details.expectedDate,
      notes: details.notes,
      branch: 'RX',
      sourceId: open.id,
    });
    awaiting = 'AWAITING RX';
    destination =
      taskStatus === 'CALL / RX' || taskStatus === 'HOLD / RX'
        ? 'CALLED FOR RX'
        : 'SAVE';
    patch.destination = destination;
    patch.followUpDate = followUp;
    patch.todo = 'HOLD';
    patch.status = 'HOLD/RX';
    if (taskStatus === 'CALL / RX') {
      patch.rxStatus = 'CALL FOR RX';
    }
  } else if (taskStatus === 'INQUIRY') {
    const followUp = details.followUpDate || addHours(now, inquiryHours);
    next = ensureAwaiting(next, 'AWAITING RESPONSE', {
      followUpDate: followUp,
      notes: details.notes,
      branch: 'INQUIRY',
      sourceId: open.id,
    });
    awaiting = 'AWAITING RESPONSE';
    destination = 'SAVE';
    patch.destination = destination;
    patch.followUpDate = followUp;
  } else if (isFollowUpTask(taskStatus)) {
    const followUp = details.followUpDate || addHours(now, inquiryHours);
    const awaitingStatus = awaitingForBranch(details.branch);
    if (awaitingStatus) {
      next = ensureAwaiting(next, awaitingStatus, {
        followUpDate: followUp,
        eta: details.eta,
        referenceNumber: details.referenceNumber,
        labVendor: details.labVendor,
        repairLocation: details.repairLocation,
        notes: details.notes,
        branch: details.branch,
      });
      awaiting = awaitingStatus;
    }
    destination = 'SAVE';
    patch.destination = destination;
    patch.followUpDate = followUp;
  } else if (taskStatus === 'ORDER FRAME') {
    const followUp = details.followUpDate || details.eta || context.eta;
    next = ensureAwaiting(next, 'AWAITING FRAME', {
      vendor: details.vendor,
      eta: details.eta,
      referenceNumber: details.referenceNumber,
      followUpDate: followUp,
      notes: details.notes,
      branch: 'FRAME',
      sourceId: open.id,
    });
    awaiting = 'AWAITING FRAME';
    destination = 'ORDERED FRAME';
    patch.destination = destination;
    patch.physicalStatus = 'ORDERED';
    patch.todo = 'ORDERED';
    patch.eta = details.eta;
    patch.referenceNumber = details.referenceNumber;
    patch.followUpDate = followUp;
    patch.jobActionsRemove = ['SEND TO STACK'];
  } else if (taskStatus === 'ORDER PART') {
    const followUp = details.followUpDate || details.eta;
    next = ensureAwaiting(next, 'AWAITING PART', {
      eta: details.eta,
      referenceNumber: details.referenceNumber,
      followUpDate: followUp,
      notes: details.notes,
      branch: 'PART',
      sourceId: open.id,
    });
    awaiting = 'AWAITING PART';
    destination = 'SAVE';
    patch.destination = destination;
    patch.eta = details.eta;
    patch.referenceNumber = details.referenceNumber;
    patch.followUpDate = followUp;
  } else if (taskStatus === 'ORDER LENSES') {
    next = ensureAwaiting(next, 'AWAITING LENSES', {
      followUpDate: details.followUpDate,
      notes: details.notes,
      branch: 'LENSES',
      sourceId: open.id,
    });
    awaiting = 'AWAITING LENSES';
    destination = 'ORDERED LENSES';
    patch.destination = destination;
    patch.lensStatus = 'ORDERED';
  } else if (outcome === 'SENT TO REPAIR' || outcome === 'SENT FOR REPAIR') {
    const followUp = details.followUpDate || details.eta;
    next = ensureAwaiting(next, 'AWAITING REPAIR', {
      labVendor: details.labVendor || details.repairLocation,
      repairLocation: details.repairLocation,
      referenceNumber: details.referenceNumber,
      eta: details.eta,
      followUpDate: followUp,
      vendor: details.vendor,
      notes: details.notes,
      repairItems: details.repairItems,
      estimate: details.estimate,
      images: details.images,
      branch: 'REPAIR',
      sourceId: open.id,
    });
    awaiting = 'AWAITING REPAIR';
    destination = 'SENT FOR REPAIR';
    patch.destination = destination;
    patch.status = 'AWAITING REPAIR';
    patch.todo = 'REPAIR';
    patch.labVendor = details.labVendor || details.repairLocation;
    patch.repairLocation = details.repairLocation;
    patch.referenceNumber = details.referenceNumber;
    patch.eta = details.eta;
    patch.followUpDate = followUp;
  } else if (outcome === 'REPAIRED') {
    destination = 'SAVE';
    patch.destination = destination;
  } else if (taskStatus === 'SHIP') {
    destination = 'SHIPPED';
    patch.destination = destination;
  }

  const logNotes = [
    details.notes,
    details.referenceNumber ? `REF. #${details.referenceNumber}` : '',
    details.labVendor,
    awaiting ? `CREATED ${awaiting}` : '',
  ]
    .filter(Boolean)
    .join(' — ');

  return {
    entries: next,
    destination,
    logItem: outcome,
    logNotes,
    patch,
  };
}

export function resolveAwaiting(
  entries: WorkflowEntry[],
  input: CompleteTaskInput,
  context: WorkflowContext = {}
): WorkflowResult {
  const initials = normalizeInitials(input.initials);
  const now = input.now || new Date();
  let next = (entries || []).map((entry) => activityEntryDefaults(entry));
  const index = locateEntry(next, input);
  const empty: WorkflowResult = {
    entries: next,
    destination: 'SAVE',
    logItem: '',
    logNotes: '',
    patch: { destination: 'SAVE' },
  };
  if (index < 0 || classifyKind(next[index]) !== 'awaiting') {
    return empty;
  }
  const awaiting = next[index];
  const status = String(awaiting.status || '').trim().toUpperCase();
  const activity = String(input.activityChoice || 'RECEIVED').trim().toUpperCase();
  const allowed = awaitingActions(status).map((value) => value.toUpperCase());
  if (activity && !allowed.includes(activity) && activity !== 'RECEIVED') {
    return empty;
  }
  const details: Partial<WorkflowEntry> = {
    notes: input.notes !== undefined ? input.notes : awaiting.notes,
    eta: input.eta !== undefined ? input.eta : awaiting.eta,
    referenceNumber:
      input.referenceNumber !== undefined
        ? input.referenceNumber
        : awaiting.referenceNumber,
    waitingOn: input.waitingOn !== undefined ? input.waitingOn : awaiting.waitingOn,
    vendor: input.vendor !== undefined ? input.vendor : awaiting.vendor,
    labVendor: input.labVendor !== undefined ? input.labVendor : awaiting.labVendor,
    branch: awaiting.branch || branchForStatus(status),
    sourceId: awaiting.id,
  };
  next = closeAwaiting(next, status);
  const branch = details.branch || branchForStatus(status);
  next = cancelFollowUpTasks(next, branch);

  let completedStatus = `${status.replace(/^AWAITING /, '')} RECEIVED`;
  let destination = 'SAVE';
  const patch: StackPatch = { destination: 'SAVE', followUpDate: '' };

  if (status === 'AWAITING RX') {
    completedStatus = 'RX RECEIVED';
    destination = 'RX RECEIVED';
    patch.destination = destination;
    patch.rxStatus = '';
    patch.jobActionsRemove = ['CALL FOR RX', 'HOLD / RX'];
    if (!context.frameOnly) {
      next = ensureOpenTask(next, 'ORDER LENSES');
    }
  } else if (status === 'AWAITING FRAME') {
    completedStatus = 'FRAME RECEIVED';
    destination = 'RECEIVED FRAME';
    patch.destination = destination;
    patch.physicalStatus = '';
  } else if (status === 'AWAITING PART') {
    completedStatus = 'PART RECEIVED';
    destination = 'SAVE';
    patch.destination = destination;
    next = ensureOpenTask(next, 'REPAIR', {
      repairItems: context.repairItems || [],
    });
  } else if (status === 'AWAITING REPAIR') {
    completedStatus = 'REPAIR RECEIVED';
    destination = 'RECEIVED FRAME';
    patch.destination = destination;
  } else if (status === 'AWAITING RESPONSE') {
    completedStatus = 'RESPONSE RECEIVED';
    destination = 'SAVE';
    patch.destination = destination;
  } else if (status === 'AWAITING LENSES') {
    completedStatus = 'LENSES RECEIVED';
    destination = 'RECEIVED LENSES';
    patch.destination = destination;
    patch.lensStatus = 'RECEIVED';
    next = ensureOpenTask(next, 'FIRST INSPECTION');
  }

  next = pushCompleted(next, completedStatus, details, initials, now);

  return {
    entries: next,
    destination,
    logItem: completedStatus,
    logNotes: String(details.notes || ''),
    patch,
  };
}

export function reopenCompleted(
  entries: WorkflowEntry[],
  input: CompleteTaskInput
): WorkflowResult {
  const initials = normalizeInitials(input.initials);
  let next = (entries || []).map((entry) => activityEntryDefaults(entry));
  const index = locateEntry(next, input);
  const empty: WorkflowResult = {
    entries: next,
    destination: 'SAVE',
    logItem: '',
    logNotes: '',
    patch: { destination: 'SAVE' },
  };
  if (index < 0 || classifyKind(next[index]) !== 'completed') {
    return empty;
  }
  const completed = next[index];
  const original = reopenTaskStatus(completed);
  if (!original) {
    return empty;
  }
  const branch = completed.branch || branchForStatus(completed.status);
  const awaitingStatus = awaitingForBranch(branch);
  if (awaitingStatus) {
    next = closeAwaiting(next, awaitingStatus);
  }
  const repairItems =
    input.repairItems !== undefined ? input.repairItems : completed.repairItems;
  next = ensureOpenTask(next, original, {
    notes: input.notes !== undefined ? input.notes : '',
    repairItems,
    description:
      input.description !== undefined ? input.description : completed.description,
    waitingOn: completed.waitingOn,
    vendor: completed.vendor,
    labVendor: completed.labVendor,
    referenceNumber: completed.referenceNumber,
    eta: completed.eta,
    followUpDate: completed.followUpDate,
    reopenedFrom: completed.id,
    branch,
  });
  return {
    entries: next,
    destination: 'SAVE',
    logItem: `REOPENED ${original}`,
    logNotes: String(input.notes || initials),
    patch: { destination: 'SAVE' },
  };
}

export function canReopenCompleted(entry: WorkflowEntry): boolean {
  return !!reopenTaskStatus(entry);
}

function reopenTaskStatus(entry: WorkflowEntry): string {
  const status = String(entry?.status || '').trim();
  if (status === 'CALLED FOR RX') {
    return 'CALL / RX';
  }
  if (isFollowedUpStatus(status)) {
    const n = followUpNumberFromStatus(status) || 1;
    return followUpTaskName(branchForStatus(status), n);
  }
  if (status === 'ORDERED' && entry.branch === 'PART') {
    return 'ORDER PART';
  }
  if (status === 'ORDERED' || status === 'ORDERED FRAME') {
    return 'ORDER FRAME';
  }
  if (status === 'ORDERED LENSES') {
    return 'ORDER LENSES';
  }
  if (status === 'REPAIRED' || status === 'SENT FOR REPAIR' || status === 'SENT TO REPAIR') {
    return 'REPAIR';
  }
  if (status === 'INQUIRED') {
    return 'INQUIRY';
  }
  if (status === 'CUSTOMER APPROVED' || status === 'LEFT MESSAGE') {
    return 'REPAIR';
  }
  if (status === 'INSTALLED NOSEPADS') {
    return 'INSTALL NOSEPADS';
  }
  if (status === 'OTHER / PART') {
    return 'OTHER / PART';
  }
  if (isWorkflowOpenStatus(status)) {
    return canonicalOpenStatus(status);
  }
  return '';
}

function awaitingForBranch(branch?: string): string {
  switch (String(branch || '').toUpperCase()) {
    case 'RX':
      return 'AWAITING RX';
    case 'FRAME':
      return 'AWAITING FRAME';
    case 'PART':
      return 'AWAITING PART';
    case 'REPAIR':
      return 'AWAITING REPAIR';
    case 'LENSES':
      return 'AWAITING LENSES';
    case 'INQUIRY':
      return 'AWAITING RESPONSE';
    default:
      return '';
  }
}

export function editAwaiting(
  entries: WorkflowEntry[],
  input: CompleteTaskInput
): WorkflowResult {
  const initials = normalizeInitials(input.initials);
  const now = input.now || new Date();
  const next = (entries || []).map((entry) => activityEntryDefaults(entry));
  const index = locateEntry(next, input);
  const empty: WorkflowResult = {
    entries: next,
    destination: 'SAVE',
    logItem: '',
    logNotes: '',
    patch: { destination: 'SAVE' },
  };
  if (index < 0 || classifyKind(next[index]) !== 'awaiting') {
    return empty;
  }
  const current = next[index];
  const updated = activityEntryDefaults({
    ...current,
    notes: input.notes !== undefined ? input.notes : current.notes,
    eta: input.eta !== undefined ? input.eta : current.eta,
    referenceNumber:
      input.referenceNumber !== undefined
        ? input.referenceNumber
        : current.referenceNumber,
    followUpDate:
      input.followUpDate !== undefined ? input.followUpDate : current.followUpDate,
    waitingOn: input.waitingOn !== undefined ? input.waitingOn : current.waitingOn,
    vendor: input.vendor !== undefined ? input.vendor : current.vendor,
    labVendor: input.labVendor !== undefined ? input.labVendor : current.labVendor,
    kind: 'awaiting',
    completed: false,
  });
  next[index] = updated;
  const changes = [
    input.followUpDate !== undefined ? 'FOLLOW-UP DATE' : '',
    input.eta !== undefined ? 'ETA' : '',
    input.waitingOn !== undefined ? 'WAITING ON' : '',
    input.referenceNumber !== undefined ? 'REFERENCE #' : '',
    input.vendor !== undefined ? 'VENDOR' : '',
    input.labVendor !== undefined ? 'REPAIR VENDOR' : '',
  ].filter(Boolean);
  return {
    entries: next,
    destination: 'SAVE',
    logItem: `${current.status} UPDATED`,
    logNotes: `${changes.join(', ')} — ${initials}`.trim(),
    patch: {
      destination: 'SAVE',
      eta: updated.eta,
      referenceNumber: updated.referenceNumber,
      followUpDate: updated.followUpDate,
      labVendor: updated.labVendor,
    },
  };
}

export function ensureDueFollowUps(
  entries: WorkflowEntry[],
  now: Date = new Date()
): WorkflowEntry[] {
  let next = (entries || []).map((entry) => activityEntryDefaults(entry));
  const today = startOfDay(now);
  awaitingRows(next).forEach((row) => {
    const due = parseActivityDate(row.entry.followUpDate) || parseActivityDate(row.entry.eta);
    if (!due) {
      return;
    }
    const dueDay = startOfDay(due);
    if (dueDay.getTime() > today.getTime()) {
      return;
    }
    const branch = row.entry.branch || branchForStatus(row.entry.status);
    const existing = next.some(
      (entry) =>
        classifyKind(entry) === 'open' &&
        isFollowUpTask(entry.status) &&
        branchForStatus(entry.status) === branch
    );
    if (existing) {
      return;
    }
    const n = nextFollowUpNumber(next, branch);
    next = ensureOpenTask(next, followUpTaskName(branch, n), {
      followUpDate: due,
      branch,
      sourceId: row.entry.id,
      followUpNumber: n,
    });
  });
  return next;
}

function shouldCreateOpenTask(
  entries: WorkflowEntry[],
  status: string,
  completedStatuses: string[]
): boolean {
  if (hasOpenTask(entries, status)) {
    return false;
  }
  const related = (entries || []).filter((entry) => {
    const value = String(entry.status || '');
    if (classifyKind(entry) !== 'completed') {
      return false;
    }
    if (completedStatuses.includes(value)) {
      return true;
    }
    return value === `REOPENED ${canonicalOpenStatus(status)}`;
  });
  if (!related.length) {
    return true;
  }
  const last = related[related.length - 1];
  return String(last.status || '').indexOf('REOPENED ') === 0;
}

export function autoCreateFromContext(
  entries: WorkflowEntry[],
  context: WorkflowContext
): WorkflowEntry[] {
  let next = (entries || []).map((entry) => activityEntryDefaults(entry));
  const actions = (context.jobActions || []).map((value) => String(value || '').trim());
  const has = (action: string) =>
    actions.includes(action) ||
    (action === 'CALL FOR RX' && actions.includes('CALL / RX'));

  if (has('CALL FOR RX') && !hasAwaiting(next, 'AWAITING RX')) {
    if (shouldCreateOpenTask(next, 'CALL / RX', ['CALLED FOR RX', 'CALL NEEDED'])) {
      next = ensureOpenTask(next, 'CALL / RX', {
        followUpDate: context.followUpDate,
        whoToCall: context.whoToCall,
        activityChoice: 'CALL NEEDED',
      });
    }
  }
  if (
    has(ORDER_LENSES_ACTION) &&
    context.lensStatus !== 'RECEIVED' &&
    !hasAwaiting(next, 'AWAITING LENSES')
  ) {
    if (shouldCreateOpenTask(next, 'ORDER LENSES', ['ORDERED LENSES'])) {
      next = ensureOpenTask(next, 'ORDER LENSES');
    }
  }
  if (has('HOLD / RX') && !hasAwaiting(next, 'AWAITING RX')) {
    if (shouldCreateOpenTask(next, 'HOLD / RX', ['CALLED FOR RX', 'RX RECEIVED'])) {
      next = ensureOpenTask(next, 'HOLD / RX', {
        followUpDate: context.followUpDate,
        expectedDate: context.expectedDate,
      });
    }
  }
  if (has('HOLD')) {
    if (shouldCreateOpenTask(next, 'HOLD', [])) {
      next = ensureOpenTask(next, 'HOLD');
    }
  }
  if (has('INQUIRY')) {
    if (shouldCreateOpenTask(next, 'INQUIRY', ['INQUIRED'])) {
      next = ensureOpenTask(next, 'INQUIRY');
    }
  }
  if (has('QUOTE')) {
    if (shouldCreateOpenTask(next, 'QUOTE', [])) {
      next = ensureOpenTask(next, 'QUOTE');
    }
  }
  const status = String(context.status || '').trim();
  const enclosed =
    !!context.enclosed || String(context.physicalStatus || '') === 'ENCLOSED';
  if (
    (has('SEND TO STACK') ||
      status === 'TO ORDER' ||
      status === 'ORDER FRAME' ||
      String(context.physicalStatus || '') === 'TO ORDER') &&
    !enclosed &&
    !hasAwaiting(next, 'AWAITING FRAME')
  ) {
    if (shouldCreateOpenTask(next, 'ORDER FRAME', ['ORDERED'])) {
      next = ensureOpenTask(next, 'ORDER FRAME', {
        eta: context.eta,
        referenceNumber: context.referenceNumber,
      });
    }
  }
  const repairItems = normalizeRepairItems(context.repairItems || []);
  if (context.nosepadInstall) {
    if (!repairItems.includes('Nosepad Install / Addition')) {
      repairItems.push('Nosepad Install / Addition');
    }
  }
  if (context.partOther) {
    if (!repairItems.includes('Other')) {
      repairItems.push('Other');
    }
  }
  if (repairItems.length || has('REPAIR')) {
    if (!hasAwaiting(next, 'AWAITING REPAIR')) {
      if (shouldCreateOpenTask(next, 'REPAIR', ['REPAIRED', 'SENT FOR REPAIR', 'SENT TO REPAIR'])) {
        next = ensureOpenTask(next, 'REPAIR', {
          repairItems,
          repairLocation: context.repairLocation || IN_HOUSE_REPAIR_LOCATION,
          description: context.partOtherDescription || '',
          otherText: context.partOtherDescription || '',
        });
      }
    }
  } else {
    const otherIndex = findOpenIndex(next, 'OTHER / PART');
    if (otherIndex >= 0) {
      next.splice(otherIndex, 1);
    }
  }
  if (context.lensStatus === 'RECEIVED') {
    const alreadyInspected = next.some(
      (entry) =>
        classifyKind(entry) === 'completed' &&
        canonicalOpenStatus(entry.status) === 'FIRST INSPECTION'
    );
    if (!alreadyInspected) {
      next = ensureOpenTask(next, 'FIRST INSPECTION');
    }
  }
  next = ensureDueFollowUps(next, context.now);
  return next;
}

export function normalizeWorkflowEntries(
  entries: WorkflowEntry[],
  context: WorkflowContext = {}
): WorkflowEntry[] {
  let next = (entries || []).map((entry) => {
    const defaults = activityEntryDefaults(entry);
    if (
      classifyKind(defaults) === 'open' &&
      canonicalOpenStatus(defaults.status) !== defaults.status
    ) {
      defaults.status = canonicalOpenStatus(defaults.status);
    }
    return defaults;
  });
  (entries || []).forEach((entry) => {
    const status = String(entry?.status || '').trim();
    if (status === 'ORDERED' && classifyKind(entry) === 'completed') {
      if (!hasAwaiting(next, 'AWAITING FRAME') && !hasOpenTask(next, 'ORDER FRAME')) {
        const orderStatus = String(context.status || '');
        if (/^AWAITING FRAME/i.test(orderStatus) || orderStatus === 'ORDERED') {
          next = ensureAwaiting(next, 'AWAITING FRAME', {
            eta: entry.eta || context.eta,
            referenceNumber: entry.referenceNumber || context.referenceNumber,
            followUpDate: context.followUpDate || entry.eta,
            branch: 'FRAME',
          });
        }
      }
    }
    if (status === 'CALLED FOR RX') {
      if (!hasAwaiting(next, 'AWAITING RX') && !hasOpenTask(next, 'CALL / RX')) {
        next = ensureAwaiting(next, 'AWAITING RX', {
          followUpDate: context.followUpDate,
          branch: 'RX',
        });
      }
    }
    if (status === 'ORDERED LENSES') {
      if (
        !hasAwaiting(next, 'AWAITING LENSES') &&
        context.lensStatus !== 'RECEIVED'
      ) {
        next = ensureAwaiting(next, 'AWAITING LENSES', { branch: 'LENSES' });
      }
    }
    if (status === 'SENT FOR REPAIR') {
      if (!hasAwaiting(next, 'AWAITING REPAIR')) {
        next = ensureAwaiting(next, 'AWAITING REPAIR', {
          labVendor: entry.labVendor || context.labVendor,
          referenceNumber: entry.referenceNumber,
          eta: entry.eta || context.eta,
          branch: 'REPAIR',
        });
      }
    }
  });
  return autoCreateFromContext(next, context);
}

export interface StackOpenTask {
  task: string;
  due?: string | Date;
}

const TODO_TODAY_TASKS = new Set([
  'CALL / RX',
  'CALL FOR RX',
  'HOLD / RX',
  'FIRST INSPECTION',
  'INSTALL NOSEPADS',
  'SHIP',
  'OTHER / PART',
  'CHECK FRAME ETA',
  'REPAIR',
  'INQUIRY',
]);

export function stackTaskDisplayName(entry: WorkflowEntry): string {
  const status = canonicalOpenStatus(entry.status);
  if (status === 'ORDER FRAME' || status === 'TO ORDER') {
    return 'TO ORDER / FRAME';
  }
  if (status === 'ORDER PART') {
    return 'TO ORDER / PART';
  }
  if (status === 'ORDER LENSES') {
    return 'TO ORDER / LENSES';
  }
  if (status === 'REPAIR' && isOutsideRepairLocation(entry.repairLocation || entry.labVendor)) {
    return 'TO SHIP / REPAIR';
  }
  if (status === 'SHIP') {
    return 'TO SHIP / WARRANTY';
  }
  return status;
}

function isTodoTodayOpenTask(entry: WorkflowEntry, now: Date = new Date()): boolean {
  const status = canonicalOpenStatus(entry.status);
  if (isFollowUpTask(status)) {
    return true;
  }
  if (status === 'REPAIR') {
    return isOutsideRepairLocation(entry.repairLocation || entry.labVendor) || needsApproval(entry);
  }
  if (status === 'HOLD / RX') {
    const due = parseActivityDate(entry.followUpDate);
    if (!due) {
      return false;
    }
    return startOfDay(due).getTime() <= startOfDay(now).getTime();
  }
  if (status === 'INQUIRY') {
    return false;
  }
  return TODO_TODAY_TASKS.has(status);
}

export function stackOpenTasksFromOrder(order: any): StackOpenTask[] {
  const activity = order?.products?.[0]?.frame?.activity;
  if (!Array.isArray(activity)) {
    return [];
  }
  return activity
    .map((entry: WorkflowEntry) => activityEntryDefaults(entry))
    .filter((entry) => classifyKind(entry) === 'open')
    .filter((entry) => isTodoTodayOpenTask(entry))
    .map((entry) => ({
      task: stackTaskDisplayName(entry),
      due: entry.followUpDate || entry.date,
    }));
}

export const REPAIR_ITEM_LIST = REPAIR_ITEM_OPTIONS;
